import { useState } from 'react';
import { Link } from 'react-router';
import { Mail, AlertCircle, Loader2, CheckCircle, ArrowLeft, Lock } from 'lucide-react';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError('Email is required'); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setError('Enter a valid email address'); return; }
    setError('');
    setLoading(true);
    await new Promise(r => setTimeout(r, 1500));
    setLoading(false);
    setSent(true);
  };

  if (sent) {
    return (
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-[#E5E7EB] text-center">
          <div className="w-16 h-16 bg-[#1E6DFF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-[#1E6DFF]" />
          </div>
          <h1 className="text-2xl text-[#2D323A] mb-2">Check your email</h1>
          <p className="text-[#717182] mb-2">We sent a password reset link to</p>
          <p className="text-[#2D323A] mb-6">{email}</p>
          <div className="bg-[#F5F7FA] rounded-xl p-4 mb-6 text-sm text-[#717182]">
            Didn't receive the email? Check your spam folder or{' '}
            <button onClick={() => setSent(false)} className="text-[#1E6DFF] hover:underline">try again</button>
          </div>
          <Link to="/auth/login" className="flex items-center justify-center gap-2 text-[#1E6DFF] hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md">
      <div className="bg-white rounded-2xl p-8 shadow-xl border border-[#E5E7EB]">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#1E6DFF]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-[#1E6DFF]" />
          </div>
          <h1 className="text-2xl text-[#2D323A] mb-2">Forgot Password?</h1>
          <p className="text-[#717182]">Enter your email and we'll send you a reset link</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm text-[#2D323A] mb-1.5">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
              <input
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                placeholder="you@example.com"
                className={`w-full pl-11 pr-4 py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors ${error ? 'border-[#EF4444]' : 'border-[#E5E7EB]'}`}
              />
            </div>
            {error && <p className="flex items-center gap-1 text-[#EF4444] text-sm mt-1"><AlertCircle className="w-4 h-4" /> {error}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <Link to="/auth/login" className="flex items-center justify-center gap-2 text-[#717182] hover:text-[#1E6DFF] mt-6 text-sm transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Sign In
        </Link>
      </div>
    </div>
  );
}

